import { useEffect, useState } from 'react';
import transportCompanyLogoService from '../services/transportCompanyLogoService';

function toViewState(entry) {
    return {
        url: entry?.objectUrl ?? null,
        missing: Boolean(entry?.missing || !entry?.objectUrl),
        error: false,
    };
}

export default function useTransportCompanyLogo(format) {
    const [state, setState] = useState(() => {
        const cached = transportCompanyLogoService.getCached(format);
        return toViewState(cached);
    });
    const [loading, setLoading] = useState(() => !transportCompanyLogoService.hasFreshCache(format));

    useEffect(() => {
        let cancelled = false;

        const apply = (entry) => {
            if (cancelled) return;
            setState(toViewState(entry));
            setLoading(false);
        };

        const load = async ({ force = false } = {}) => {
            if (!force && transportCompanyLogoService.hasFreshCache(format)) {
                apply(transportCompanyLogoService.getCached(format));
                return;
            }

            setLoading(true);
            try {
                const entry = await transportCompanyLogoService.get(format, { force });
                apply(entry);
            } catch {
                if (!cancelled) {
                    setState({ url: null, missing: true, error: true });
                    setLoading(false);
                }
            }
        };

        load();

        const unsubscribe = transportCompanyLogoService.subscribe(() => {
            if (transportCompanyLogoService.hasFreshCache(format)) {
                apply(transportCompanyLogoService.getCached(format));
                return;
            }
            load({ force: true });
        });

        return () => {
            cancelled = true;
            unsubscribe();
        };
    }, [format]);

    return {
        url: state.url,
        missing: state.missing,
        loading,
        error: state.error,
    };
}
